/**
 * Regler för förslagsformuläret (BL-169, docs/visions/pa-gang/forslag-brygga-spec.md v2).
 * Samma gränser kontrolleras på servern (backend/app/services/feedback_regler.py).
 */

/** Typerna i segmentkontrollen. `typ` är värdet till API:t, `forslagstyp` skiljer Ny funktion från Förbättring. */
export const TYPER = [
  { key: 'bug', typ: 'bug', label: 'Buggrapport', titel: 'Rapportera bugg', knapp: 'Skicka buggrapport' },
  { key: 'improvement', typ: 'improvement', label: 'Förbättring', titel: 'Föreslå förbättring', knapp: 'Skicka förslag' },
  { key: 'ny_funktion', typ: 'improvement', forslagstyp: 'ny_funktion', label: 'Ny funktion', titel: 'Föreslå ny funktion', knapp: 'Skicka förslag' },
  { key: 'question', typ: 'question', label: 'Fråga', titel: 'Ställ en fråga', knapp: 'Skicka fråga' },
];

export const KALLOR = ['sidfot', 'fab', 'pa_gang'];

export const MAX_RUBRIK = 90;
export const MAX_BESKRIVNING = 5000;
export const MAX_FILER = 10;
export const MAX_FIL_BYTES = 25 * 1024 * 1024;
export const TILLATNA_ANDELSER = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'pdf', 'zip', 'md', 'txt', 'html', 'docx', 'xlsx', 'csv', 'json'];
export const ACCEPT = TILLATNA_ANDELSER.map((a) => `.${a}`).join(',');

export const MODULER = [
  'Ekonomi', 'Ekonomi – Betalplan', 'Ekonomi – BÅK/Mängdavstämning',
  'Tidplan', 'Avvikelsehantering', 'Dagbok', 'Inköpsmodul',
  'Ärendehantering', 'Anbudsmodul', 'Leverantörsmodul', 'Avtalsmodul',
  'Bilder', 'Ritningar', 'Skyddsronder', 'KanAI', 'Kalkylmodul',
  'Maskinpark', 'På gång', 'Feedback', 'Plattformen generellt', 'Övrigt',
];

export const ALLVARLIGHET = [
  { key: 'critical', label: 'Kritisk' },
  { key: 'high', label: 'Hög' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Låg' },
];

export const PAVERKAN = [
  { key: 'all_users', label: 'Alla användare' },
  { key: 'team', label: 'Flera i teamet' },
  { key: 'individual', label: 'Bara jag' },
];

export const PLATSHALLARE = {
  bug: 'Vad gjorde du, vad hände och vad borde ha hänt i stället?',
  improvement: 'Vad fungerar i dag, vad skulle bli bättre och varför?',
  ny_funktion: 'Vad vill du kunna göra, och vilken nytta ger det?',
  question: 'Vad undrar du över?',
};

export function typFor(key) {
  return TYPER.find((t) => t.key === key) || TYPER[0];
}

export function andelse(namn) {
  const i = String(namn || '').lastIndexOf('.');
  return i === -1 ? '' : String(namn).slice(i + 1).toLowerCase();
}

/**
 * Delar upp filer i godkända och fel. `befintliga` är antalet redan valda filer.
 * @param {File[]} filer
 * @param {number} befintliga
 * @returns {{ ok: File[], fel: string[] }}
 */
export function sorteraFiler(filer, befintliga) {
  const ok = [];
  const fel = [];
  Array.from(filer || []).forEach((f) => {
    if (!TILLATNA_ANDELSER.includes(andelse(f.name))) fel.push(`${f.name}: filtypen tas inte emot.`);
    else if (f.size > MAX_FIL_BYTES) fel.push(`${f.name}: filen är större än 25 MB.`);
    else if (befintliga + ok.length >= MAX_FILER) fel.push(`${f.name}: högst ${MAX_FILER} filer per förslag.`);
    else ok.push(f);
  });
  return { ok, fel };
}

/** Tar bort frågedelen ur en adress, så att inga nycklar i länkar följer med. */
export function utanFragedel(url) {
  return String(url || '').split('?')[0].split('#')[0];
}

/** Rensar nycklar och frågedelar ur en loggrad, som kan innehålla vad som helst. */
export function tvattaLogg(text) {
  return String(text || '')
    .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[borttaget]')
    .replace(/kanai_pat_[\w.]+/g, '[borttaget]')
    .replace(/\b(authorization|set-cookie|cookie)\b\s*[:=].*$/gim, '$1 [borttaget]') // hela värdet, även flera par
    .replace(/\bbearer\s+\S+/gi, 'Bearer [borttaget]')
    .replace(/(token|password|lösenord|nyckel)(\s*[:=]\s*|\s+)\S+/gi, '$1 [borttaget]')
    .replace(/(https?:\/\/[^\s?"']+)\?[^\s"']*/g, '$1')
    .slice(0, 500);
}

/**
 * Bygger anropet till POST /api/v1/feedback.
 * @param {{ typKey: string, rubrik: string, beskrivning: string, modul: string, allvarlighet: string, paverkan: string }} v
 * @param {object} ctx automatisk kontext från useIssueContext
 * @param {string} kalla sidfot | fab | pa_gang
 * @param {string} appVersion
 */
export function byggAnrop(v, ctx, kalla, appVersion) {
  const t = typFor(v.typKey);
  const kontext = {
    ...(ctx || {}),
    module: v.modul || null, // annars faller servern tillbaka på "Övrigt" från adressen
    url: utanFragedel(ctx && ctx.url),
    recent_api_calls: ((ctx && ctx.recent_api_calls) || []).map((a) => ({ ...a, url: utanFragedel(a.url) })),
    console_errors: ((ctx && ctx.console_errors) || []).map((c) => ({ ...c, message: tvattaLogg(c.message) })),
    kalla: KALLOR.includes(kalla) ? kalla : 'fab',
    app_version: appVersion || null,
    tidpunkt: new Date().toISOString(),
    forslagstyp: t.forslagstyp || null,
  };
  const anrop = {
    type: t.typ,
    title: v.rubrik.trim(),
    description: v.beskrivning.trim() || null,
    module: v.modul || null,
    context: kontext,
  };
  if (t.typ === 'bug') anrop.severity = v.allvarlighet || 'medium';
  if (t.typ === 'improvement') anrop.impact = v.paverkan || 'team';
  return anrop;
}
